import {
  loc_approximateLocation,
  loc_preciseLocation,
  loc_primary,
  Locale
} from '@cumu/strings';
import { copyIcon, geoApproximateIcon, geoIcon, icon } from '../icons';
import {
  AddressFields,
  AddressPrecision,
  isPreciseAddress
} from '../model/address';
import { Contact, ContactType } from '../model/contact';
import { EmailType } from '../model/email';
import { PhoneType } from '../model/phone';
import { attr, html, TemplateResult } from '../template';

export function personName({
  last_name,
  first_name,
  middle_name,
  suffix,
  nickname
}: {
  last_name: string;
  first_name: string;
  middle_name: string;
  suffix: string;
  nickname: string;
}): string {
  return (
    (first_name ? first_name + ' ' : '') +
    (middle_name ? middle_name + ' ' : '') +
    (nickname ? `"${nickname}" ` : '') +
    last_name +
    (suffix ? ', ' + suffix : '')
  );
}

export function personFullName({
  last_name,
  first_name,
  middle_name,
  suffix,
  nickname
}: {
  last_name: string;
  first_name: string;
  middle_name: string;
  suffix: string;
  nickname: string;
}): string {
  return (
    last_name +
    (first_name || middle_name || nickname
      ? ', ' +
        first_name +
        (first_name && nickname ? ' ' : '') +
        (nickname ? `"${nickname}"` : '') +
        ((first_name || nickname) && middle_name
          ? ' ' + middle_name
          : middle_name)
      : '') +
    (suffix ? ', ' + suffix : '')
  );
}

function getAddressQuery(address: Partial<AddressFields>) {
  const {
    street_address,
    sub_address,
    city,
    region_abbr,
    postal_code,
    postal_code_ext
  } = address;
  let query = street_address || '';
  if (street_address && sub_address) {
    query += (query ? ', ' : '') + sub_address;
  }
  if (city) {
    query += (query ? ', ' : '') + city;
  }
  if (region_abbr) {
    query += (query ? ', ' : '') + region_abbr;
  }
  if (postal_code) {
    query +=
      (query ? ' ' : '') +
      postal_code +
      (postal_code_ext ? '-' + postal_code_ext : '');
  }
  return query;
}

function getMapUrl(
  address: Partial<AddressFields>,
  type: 'search' | 'directions'
) {
  const query = getAddressQuery(address);
  const url = new URL(
    type === 'search'
      ? 'https://www.google.com/maps/search/'
      : 'https://www.google.com/maps/dir/'
  );
  url.searchParams.set('api', '1');
  url.searchParams.set(type === 'search' ? 'query' : 'destination', query);
  return url;
}

export function addressTemplate(
  address: Partial<AddressFields>,
  link: boolean | 'primary' | 'secondary' | 'default' = true,
  omitSubregion = false
): (string | TemplateResult)[] {
  const {
    street_address,
    sub_address,
    city,
    region_abbr,
    postal_code,
    postal_code_ext,
    subregion
  } = address;
  let formatted = street_address || '';
  if (sub_address) {
    formatted += (formatted ? ', ' : '') + sub_address;
  }
  if (city) {
    formatted += (formatted ? ', ' : '') + city;
  }
  if (region_abbr) {
    formatted += (formatted ? ', ' : '') + region_abbr;
  }
  if (postal_code) {
    formatted +=
      (formatted ? ' ' : '') +
      postal_code +
      (postal_code_ext ? '-' + postal_code_ext : '');
  }
  const parts: (string | TemplateResult)[] = [];
  if (formatted) {
    if (link) {
      let linkClass: string | undefined;
      if (link === true || link === 'primary') {
        linkClass = 'Link--primary';
      } else if (link === 'secondary') {
        linkClass = 'Link--secondary';
      }
      parts.push(
        html`<a
          ${attr('class', linkClass)}
          href="${getMapUrl(address, 'search')}"
          >${formatted}</a
        >`
      );
    } else {
      parts.push(formatted);
    }
  }
  if (subregion && !omitSubregion) {
    if (parts.length) {
      parts.push(' - ');
    }
    parts.push(html`<span class="no-wrap">County: ${subregion}</span>`);
  }
  return parts;
}

export function addressTextTemplate(
  address: Partial<AddressFields>,
  includeSubregion = true
): string {
  const {
    street_address,
    sub_address,
    city,
    region_abbr,
    postal_code,
    postal_code_ext,
    subregion
  } = address;
  let formatted = street_address || '';
  if (sub_address) {
    formatted += (formatted ? ', ' : '') + sub_address;
  }
  if (city) {
    formatted += (formatted ? ', ' : '') + city;
  }
  if (region_abbr) {
    formatted += (formatted ? ', ' : '') + region_abbr;
  }
  if (postal_code) {
    formatted +=
      (formatted ? ' ' : '') +
      postal_code +
      (postal_code_ext ? '-' + postal_code_ext : '');
  }
  if (subregion && includeSubregion) {
    if (formatted) {
      formatted += ' - ';
    }
    formatted += `County: ${subregion}`;
  }
  return formatted;
}

export function phoneTemplate(
  phone: {
    type?: PhoneType;
    number: string;
    extension: string;
  },
  link: boolean | 'primary' | 'secondary' | 'default' = true
) {
  if (link) {
    let linkClass: string | undefined;
    if (link === true || link === 'primary') {
      linkClass = 'Link--primary';
    } else if (link === 'secondary') {
      linkClass = 'Link--secondary';
    }
    return html`${
        phone.type
          ? html`<span class="color-fg-muted">${phone.type + ' '}</span>`
          : null
      }<a
        ${attr('class', linkClass)}
        href="tel:${phone.number.replaceAll(/[^\d]/g, '')}"
        >${phone.number}${phone.extension ? ' ext ' + phone.extension : ''}</a
      >`;
  }
  return `${phone.type ? phone.type + ': ' : ''}${phone.number}${
    phone.extension ? ' ext ' + phone.extension : ''
  }`;
}

export function emailTemplate(
  email: { type?: EmailType; email: string },
  link: boolean | 'primary' | 'secondary' | 'default' = true,
  copy = true
) {
  const copyButton = copy
    ? html`&nbsp;<clipboard-copy
          class="btn-octicon d-print-none ml-n1 mt-n1 mb-n1 mr-n1 f6"
          style="vertical-align: baseline;"
          aria-label="Copy email"
          value="${email.email}"
          >${icon(copyIcon)}</clipboard-copy
        >`
    : null;
  if (link) {
    let linkClass: string | undefined;
    if (link === true || link === 'primary') {
      linkClass = 'Link--primary';
    } else if (link === 'secondary') {
      linkClass = 'Link--secondary';
    }
    return html`${
        email.type
          ? html`<span class="color-fg-muted">${email.type + ' '}</span>`
          : null
      }<a ${attr('class', linkClass)} href="mailto:${email.email}"
        >${email.email}</a
      >${copyButton}`;
  }
  return html`${email.type ? email.type + ': ' : ''}${email.email}${copyButton}`;
}

export function contactTemplate(
  locale: Locale,
  item: Contact,
  contactTypes: { id: ContactType; label: Record<Locale, string> }[],
  link: boolean | 'primary' | 'secondary' | 'default' = 'secondary'
) {
  return html`<span class="d-flex flex-column gap-1 lh-condensed">
    <span class="d-flex gap-1 flex-items-center flex-wrap">
      <span>${item.name}</span>
      ${contactTypes
        .filter(
          x => x.id & item.type && x.id !== ContactType.PrimaryEmergencyContact
        )
        .map(
          ({ id, label }) =>
            html`<span class="Label"
              >${
                id === ContactType.EmergencyContact
                  ? html`<span
                        class="d-item-has-primary-emergency-contact-checked"
                        >${loc_primary[locale]}</span
                      >${' '}`
                  : null
              }${label[locale]}</span
            >`
        )}
    </span>
    ${
      item.description
        ? html`<span class="f6">${item.description}</span>`
        : null
    }
    <span class="f6 color-fg-muted d-flex flex-wrap gap-2 d-none-empty"
      >${item.phones?.map(
        x => html`<span>${phoneTemplate(x, link)}</span>`
      )}${item.emails?.map(
        x => html`<span>${emailTemplate(x, link)}</span>`
      )}</span
    >
    ${item.addresses?.map(
      x =>
        html`<span class="f6 color-fg-muted">${addressTemplate(x, link)}</span>`
    )}
  </span>`;
}

export function addressPrecisionButtonContent(
  locale: Locale,
  precision: string | undefined
) {
  const precise = isPreciseAddress((precision as AddressPrecision) ?? '');
  const text = precise
    ? loc_preciseLocation[locale]
    : loc_approximateLocation[locale];
  const iconContent = precise ? geoIcon : geoApproximateIcon;
  const classname = precise ? 'text-bold color-fg-success' : 'color-fg-muted';
  return html`<span class="d-inline-flex gap-1 flex-items-center ${classname}"
    >${icon(iconContent)}<span>${text}</span></span
  >`;
}
